import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function ComparePlans() {
  const navigate = useNavigate();
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const { t, i18n } = useTranslation();

  const locale = (() => {
    const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
  })();

  const formatPrice = (amount) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  const plans = {
    standard: {
      name: t("comparePlans.plan.standard.name"),
      price: formatPrice(29),
      target: t("comparePlans.plan.standard.target"),
      color: "neutral",
    },
    premium: {
      name: t("comparePlans.plan.premium.name"),
      price: formatPrice(199),
      target: t("comparePlans.plan.premium.target"),
      color: "black",
    },
    art_advisor: {
      name: t("comparePlans.plan.artAdvisor.name"),
      price: formatPrice(99),
      target: t("comparePlans.plan.artAdvisor.target"),
      color: "neutral",
    },
    art_advisor_premium: {
      name: t("comparePlans.plan.artAdvisorPremium.name"),
      price: formatPrice(199),
      target: t("comparePlans.plan.artAdvisorPremium.target"),
      color: "black",
    },
    family_office: {
      name: t("comparePlans.plan.familyOffice.name"),
      price: formatPrice(299),
      target: t("comparePlans.plan.familyOffice.target"),
      color: "neutral",
    },
    fondation: {
      name: t("comparePlans.plan.foundation.name"),
      price: t("comparePlans.plan.foundation.price"),
      target: t("comparePlans.plan.foundation.target"),
      color: "neutral",
      contact: true,
      contactLabel: t("comparePlans.plan.foundation.contact"),
    },
  };

  const categories = [
    {
      name: t("comparePlans.cat.collection"),
      features: [
        {
          name: t("comparePlans.features.collection.artworks"),
          standard: t("comparePlans.value.unlimited"),
          premium: t("comparePlans.value.unlimited"),
          art_advisor: t("comparePlans.value.unlimited"),
          art_advisor_premium: t("comparePlans.value.unlimited"),
          family_office: t("comparePlans.value.unlimited"),
          fondation: t("comparePlans.value.unlimited"),
         },
        {
          name: t("comparePlans.features.collection.artistsLocations"),
          standard: true,
          premium: true,
          art_advisor: true,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.collection.provenance"),
          standard: true,
          premium: true,
          art_advisor: true,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.collection.photosMultiple"),
          standard: "5",
          premium: t("comparePlans.value.unlimited"),
          art_advisor: t("comparePlans.value.unlimited"),
          art_advisor_premium: t("comparePlans.value.unlimited"),
          family_office: t("comparePlans.value.unlimited"),
          fondation: t("comparePlans.value.unlimited"),
        },
      ],
    },
    {
      name: t("comparePlans.cat.ai"),
      features: [
        {
          name: t("comparePlans.features.ai.visualAnalysis"),
          standard: false,
          premium: true,
          art_advisor: t("comparePlans.value.limited"),
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.ai.conservation"),
          standard: false,
          premium: true,
          art_advisor: t("comparePlans.value.limited"),
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.ai.inheritanceMatching"),
          standard: false,
          premium: true,
          art_advisor: false,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.ai.mediaWatch"),
          standard: false,
          premium: true,
          art_advisor: false,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
      ],
    },
    {
      name: t("comparePlans.cat.multiClient"),
      features: [
        {
          name: t("comparePlans.features.multiClient.collectionsCount"),
          standard: "1",
          premium: "1",
          art_advisor: "5",
          art_advisor_premium: t("comparePlans.value.unlimited"),
          family_office: t("comparePlans.value.unlimited"),
          fondation: t("comparePlans.value.unlimited"),
        },
        {
          name: t("comparePlans.features.multiClient.multiPortfolio"),
          standard: false,
          premium: false,
          art_advisor: true,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.multiClient.clientInvites"),
          standard: false,
          premium: false,
          art_advisor: true,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.multiClient.coBrandedReports"),
          standard: false,
          premium: false,
          art_advisor: t("comparePlans.value.basic"),
          art_advisor_premium: t("comparePlans.value.advanced"),
          family_office: t("comparePlans.value.custom"),
          fondation: t("comparePlans.value.custom"),
        },
      ],
    },
    {
      name: t("comparePlans.cat.reporting"),
      features: [
        {
          name: t("comparePlans.features.reporting.pdfExports"),
          standard: true,
          premium: true,
          art_advisor: true,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.reporting.taxReports"),
          standard: t("comparePlans.value.france"),
          premium: t("comparePlans.value.france_ch"),
          art_advisor: t("comparePlans.value.france_ch"),
          art_advisor_premium: t("comparePlans.value.multiCountry"),
          family_office: t("comparePlans.value.multiCountry"),
          fondation: t("comparePlans.value.multiCountry"),
        },
        {
          name: t("comparePlans.features.reporting.customDashboards"),
          standard: false,
          premium: t("comparePlans.value.limited"),
          art_advisor: t("comparePlans.value.limited"),
          art_advisor_premium: true,
          family_office: t("comparePlans.value.custom"),
          fondation: t("comparePlans.value.custom"),
        },
        {
          name: t("comparePlans.features.reporting.accountingExports"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
      ],
    },
    {
      name: t("comparePlans.cat.integrations"),
      features: [
        {
          name: t("comparePlans.features.integrations.api"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: false,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.integrations.webhooks"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.integrations.erp"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: false,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.integrations.sso"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: false,
          family_office: true,
          fondation: true,
        },
      ],
    },
    {
      name: t("comparePlans.cat.support"),
      features: [
        {
          name: t("comparePlans.features.support.email"),
          standard: "48h",
          premium: "24h",
          art_advisor: "12h",
          art_advisor_premium: "4h",
          family_office: "2h",
          fondation: "2h",
        },
        {
          name: t("comparePlans.features.support.accountManager"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: true,
          family_office: true,
          fondation: true,
        },
        {
          name: t("comparePlans.features.support.training"),
          standard: t("comparePlans.value.videos"),
          premium: t("comparePlans.value.videos"),
          art_advisor: t("comparePlans.value.session1"),
          art_advisor_premium: t("comparePlans.value.session2"),
          family_office: t("comparePlans.value.custom"),
          fondation: t("comparePlans.value.custom"),
        },
        {
          name: t("comparePlans.features.support.sla"),
          standard: false,
          premium: false,
          art_advisor: false,
          art_advisor_premium: "99%",
          family_office: "99.9%",
          fondation: "99.9%",
        },
      ],
    },
  ];

  const renderValue = (value) => {
    if (typeof value === "boolean") {
      return value ? (
        <span className="text-green-600 text-xl">✓</span>
      ) : (
        <span className="text-neutral-300 text-xl">−</span>
      );
    }
    return <span className="text-sm text-neutral-700">{value}</span>;
  };

  const visibleCategories = showAllFeatures ? categories : categories.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-b from-neutral-50 to-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1
              className="text-5xl md:text-6xl font-serif mb-6 tracking-tight"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("comparePlans.title")}
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              {t("comparePlans.subtitle")}
            </p>
          </div>
        </div>
      </div>

      {/* Plan headers */}
      <div className="max-w-7xl mx-auto px-6 py-12 sticky top-0 bg-white border-b border-neutral-200 z-10">
        <div className="overflow-x-auto -mx-6 px-6">
          <div className="grid grid-cols-7 gap-4 min-w-[1100px]">
            <div className="col-span-1"></div>
            {Object.entries(plans).map(([key, plan]) => (
              <div
                key={key}
                className={`text-center p-4 rounded-lg ${
                  plan.color === "black" ? "bg-black text-white" : "bg-neutral-50"
                }`}
              >
                <h3 className="font-bold text-lg mb-1">{plan.name}</h3>
                <p className="text-2xl font-bold mb-1">{plan.price}</p>
                <p className="text-xs opacity-70">{plan.target}</p>
                {plan.contact && (
                  <button
                    onClick={() => navigate("/contact")}
                    className="mt-2 px-3 py-1 text-xs border border-neutral-400 rounded-full hover:bg-neutral-200 transition-colors"
                  >
                    {plan.contactLabel}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {visibleCategories.map((category, categoryIdx) => (
          <div key={categoryIdx} className="mb-12">
            <h2
              className="text-2xl font-serif mb-6 pb-3 border-b-2 border-black"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {category.name}
            </h2>
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="space-y-2 min-w-[1100px]">
                {category.features.map((feature, featureIdx) => (
                  <div
                    key={featureIdx}
                    className="grid grid-cols-7 gap-4 py-4 border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
                  >
                    <div className="col-span-1 font-medium text-sm text-neutral-700 flex items-center">
                      {feature.name}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.standard)}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.premium)}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.art_advisor)}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.art_advisor_premium)}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.family_office)}
                    </div>
                    <div className="text-center flex items-center justify-center">
                      {renderValue(feature.fondation)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Bouton pour afficher plus */}
        {!showAllFeatures && (
          <div className="text-center py-8">
            <button
              onClick={() => setShowAllFeatures(true)}
              className="px-8 py-3 border-2 border-black text-black rounded-full hover:bg-black hover:text-white transition-all duration-300"
            >
              {t("comparePlans.showAll")}
            </button>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-neutral-50 border-t border-neutral-200 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2
            className="text-4xl font-serif mb-6"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {t("comparePlans.cta.title")}
          </h2>
          <p className="text-xl text-neutral-500 mb-8">
            {t("comparePlans.cta.subtitle")}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate("/pricing")}
              className="px-10 py-4 bg-black text-white rounded-full font-semibold hover:bg-neutral-800 transition-all duration-300"
            >
              {t("comparePlans.cta.details")}
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-10 py-4 border-2 border-neutral-300 text-neutral-700 rounded-full font-semibold hover:border-black hover:text-black transition-all duration-300"
            >
              {t("comparePlans.cta.expert")}
            </button>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <h3
          className="text-3xl font-serif mb-8 text-center"
          style={{ fontFamily: "Georgia, serif" }}
        >
          {t("comparePlans.faq.title")}
        </h3>
        <div className="space-y-6">
          <FAQItem
            question={t("comparePlans.faq.q1")}
            answer={t("comparePlans.faq.a1")}
          />
          <FAQItem
            question={t("comparePlans.faq.q2")}
            answer={t("comparePlans.faq.a2")}
          />
          <FAQItem
            question={t("comparePlans.faq.q3")}
            answer={t("comparePlans.faq.a3")}
          />
          <FAQItem
            question={t("comparePlans.faq.q4")}
            answer={t("comparePlans.faq.a4")}
          />
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-neutral-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left flex items-start justify-between gap-4 group"
      >
        <span className="font-semibold text-lg group-hover:text-neutral-600 transition-colors">
          {question}
        </span>
        <svg
          className={`w-5 h-5 flex-shrink-0 transition-transform mt-1 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <p className="mt-4 text-neutral-600 leading-relaxed">{answer}</p>
      )}
    </div>
  );
}
